import React from 'react';
import Collapse from '@mui/material/Collapse';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import cx from 'classnames';
import ShowHideToggleIcon from '@/components/common/ShowHideToggleIcon';
import translate from '@/lib/intl/translate';
import { ReactComponent as Arrow } from '../../assets/arrow.svg';
import type CSVHeaderColumn from '../../types/CSVHeaderColumn';
import type CSVRow from '../../types/CSVRow';
import MatchHeadersAutocomplete from './MatchHeadersAutocomplete';
import { useMatchHeadersContext } from './MatchHeadersProvider';

type SampleData = {
  value: string;
  row: number;
}[];

function getSampleData(
  rows: MatchHeadersTableRowProps['rows'],
  index: MatchHeadersTableRowProps['index'],
) {
  let count = 0;
  const sample: SampleData = [];

  for (let i = 0, l = rows.length; i < l; i += 1) {
    if (rows[i][index]) {
      sample.push({
        value: rows[i][index],
        row: i + 1,
      });
      count += 1;
    }

    if (count === 5) {
      break;
    }
  }
  return sample;
}

export type MatchHeadersTableRowProps = {
  index: number;
  header: CSVHeaderColumn;
  rows: CSVRow[];
};

export default function MatchHeadersTableRow({
  index,
  header,
  rows,
}: MatchHeadersTableRowProps) {
  const { matchedHeaders } = useMatchHeadersContext();
  const [isOpen, setIsOpen] = React.useState(false);
  const [isCollapseRowHidden, setIsCollapseRowHidden] = React.useState(true);
  const [isHeaderMatched, setIsHeaderMatched] = React.useState(false);

  React.useEffect(() => {
    setIsHeaderMatched(
      !!matchedHeaders.find(
        ([, csvHeaderIndex]) => csvHeaderIndex === header.index,
      ),
    );
  }, [matchedHeaders]);

  const sampleData = React.useMemo<SampleData>(
    () => (header.hasData ? getSampleData(rows, index) : []),
    [header, index, rows],
  );

  const handleShowHideToggle = React.useCallback(() => {
    if (!isOpen) {
      setIsCollapseRowHidden(false);
    }
    setIsOpen((prev) => !prev);
  }, [isOpen]);

  const handleOnExited = () => setIsCollapseRowHidden(true);
  const isParentEven = !!(index % 2);
  const hasSampleData = !!sampleData.length;

  return (
    <>
      <TableRow
        className={cx({
          'bg-grey-100': !isParentEven,
          'bg-white': !isHeaderMatched && isParentEven,
          'bg-success-light': isHeaderMatched,
        })}
        data-testid={`match-headers-table-row-${index}`}
      >
        {/* Use colSpan (along with cols tags) to set min width for first row. */}
        <TableCell className="border-b-0 px-4 py-6" colSpan={2}>
          <div className="flex items-center justify-between">
            {header.name}
            {hasSampleData && (
              <button
                className="ml-2 shrink-0"
                data-testid="match-table-headers-show-example"
                onClick={handleShowHideToggle}
                type="button"
              >
                <ShowHideToggleIcon
                  classes={{
                    icon: 'order-2',
                    root: 'flex items-center',
                    text: 'order-1 text-[13px]',
                  }}
                  isOpen={isOpen}
                  hideText={translate('HIDE_SAMPLE')}
                  showText={translate('SHOW_SAMPLE')}
                />
              </button>
            )}
          </div>
        </TableCell>
        <TableCell className="w-28 border-b-0 px-0 py-6 text-center">
          <Arrow className="inline-block" />
        </TableCell>
        <TableCell className="w-2/5 border-b-0 py-3 px-3">
          {hasSampleData ? (
            <MatchHeadersAutocomplete header={header} />
          ) : (
            <strong className="body-type2">
              {translate('DATA_UPLOADS_NO_COLUMN_DATA')}
            </strong>
          )}
        </TableCell>
      </TableRow>
      <TableRow className={cx('p-0', { hidden: isCollapseRowHidden })}>
        <TableCell className="border-b-0 p-0" colSpan={4}>
          <Collapse
            in={isOpen}
            onExited={handleOnExited}
            timeout="auto"
            unmountOnExit
          >
            {sampleData.map((sample, i) => {
              const isEven = !!(i % 2);

              return (
                <div
                  key={sample.row}
                  className={cx(
                    'flex items-center justify-between py-2 px-4 type-body1',
                    {
                      'bg-grey-100': isEven !== isParentEven,
                      'bg-white': isEven === isParentEven,
                    },
                  )}
                >
                  <div>{sample.value}</div>
                  <div>
                    {translate('DATA_UPLOADS_ROW_NUM', { num: sample.row })}
                  </div>
                </div>
              );
            })}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
